/**
 * Storage key registry for Profiteia frontend.
 *
 * Persistence has TWO contracts:
 *
 * 1. **Session-scoped (cleared on logout)**: keys prefixed with `pf_` via the
 *    `PERSISTED_STATE_NAMESPACE` constant. `clearAllPersistedState()` in
 *    `src/composables/usePersistedState.js` wipes every `pf_*` key from
 *    localStorage. `useAuthStore.logoutUser()` calls it.
 *
 *    Use `usePersistedState(key, default)` for reactive persisted refs, or
 *    one of the constants below if you need to read/write `localStorage`
 *    directly (e.g. inside store actions or non-component code).
 *
 * 2. **Out-of-namespace exceptions**: keys that intentionally survive logout
 *    or have their own lifecycle. These are listed under
 *    `OUT_OF_NAMESPACE_KEYS` for documentation; they should NOT be migrated
 *    into `pf_*` without coordinating with the owning code path.
 *
 * Why this file exists: before this registry, persistence keys were defined
 * inline in each consumer. Reviewing what gets wiped on logout required
 * grepping the codebase. Now there's a single place to audit.
 */

import { PERSISTED_STATE_NAMESPACE } from '@/composables/usePersistedState';

// ─────────────────────────────────────────────────────────────────────────
// SESSION-SCOPED KEYS (pf_*)
// Cleared on logout via clearAllPersistedState().
// ─────────────────────────────────────────────────────────────────────────

/** User's last-selected battery config in the bidding-strategy / forward-benchmark tools. */
export const BATTERY_CONFIG_KEY = `${PERSISTED_STATE_NAMESPACE}tools.lastSelectedBatteryConfigId`;

/** Last route the user visited while authenticated. Used by authRedirect to land them back. */
export const LAST_AUTHENTICATED_ROUTE_KEY = `${PERSISTED_STATE_NAMESPACE}lastAuthenticatedRoute`;

/** Explicit portal preview opt-in and once-per-tab browsing prompt (sessionStorage). */
export const DEMO_MODE_KEY = 'pf_portal_demo';
export const DEMO_BROWSING_KEY = 'pf_demo_browsing';
/** Browser-only battery presets, separate from the customer's server-side selection. */
export const DEMO_BATTERY_CONFIGS_KEY = 'pf_demo_battery_configs';
export const DEMO_BATTERY_SELECTION_KEY = 'pf_demo_battery_selection';

/** Tab-scoped route to restore after Stripe subscription checkout returns. */
export const STRIPE_SUBSCRIPTION_RETURN_PATH_KEY = 'stripeSubscriptionReturnPath';

/** Sidebar collapsed state. Auto-migrated from legacy `sidebarCollapsed`. */
export const SIDEBAR_COLLAPSED_KEY = `${PERSISTED_STATE_NAMESPACE}sidebar-collapsed`;

/** Active app locale. Auto-migrated from legacy `locale`. */
export const LOCALE_KEY = `${PERSISTED_STATE_NAMESPACE}locale`;

/** User-created visualization library (CRUD store; see lib/visualizationStore.js). */
export const USER_VISUALIZATIONS_KEY = `${PERSISTED_STATE_NAMESPACE}user_visualizations`;

/** Last-active API documentation navigation view (streams vs category). */
export const API_DOCS_VIEW_MODE_KEY = `${PERSISTED_STATE_NAMESPACE}api-docs-view-mode`;

/** Timestamp of the last test-notification send (client-side cooldown). */
export const TEST_NOTIFICATION_LAST_SEND_KEY = `${PERSISTED_STATE_NAMESPACE}test_notification.last_sent_at`;
/** `usePersistedState`-compatible suffix (without the pf_ prefix). */
export const TEST_NOTIFICATION_LAST_SEND_SUFFIX = 'test_notification.last_sent_at';

/** Reviewed limit/recharge requests awaiting scoped readback. */
export const TOKEN_SETTINGS_COMMAND_PREFIX = `${PERSISTED_STATE_NAMESPACE}admin.token-settings:`;

/** Reviewed token movements awaiting scoped readback. */
export const TOKEN_ADJUSTMENT_COMMAND_PREFIX = `${PERSISTED_STATE_NAMESPACE}admin.token-adjustment:`;

/** Endpoint hash of the currently-linked web push subscription. */
export const CURRENT_WEB_PUSH_KEY = `${PERSISTED_STATE_NAMESPACE}web_push.endpoint_hash`;
/** `usePersistedState`-compatible suffix (without the pf_ prefix). */
export const CURRENT_WEB_PUSH_SUFFIX = 'web_push.endpoint_hash';

/** Persisted form state for the imbalance-benchmark tool. */
export const IMBALANCE_BENCHMARK_INPUTS_KEY = `${PERSISTED_STATE_NAMESPACE}tools.imbalance_benchmark.inputs`;

/** Persisted form state for the power-market-simulator tool (v3 schema). */
export const POWER_MARKET_SIMULATOR_INPUTS_KEY = `${PERSISTED_STATE_NAMESPACE}tools.power_market_simulator.inputs.v3`;

/**
 * Build the per-chart settings key.
 *
 * Used by `src/components/visualizations/graph/utils/storage.js` to persist
 * legend/zoom/tooltip preferences keyed to a specific dataset visualization
 * on a specific route.
 */
export function getChartSettingsKey(pagePath, datasetId, visualizationId) {
  return `${PERSISTED_STATE_NAMESPACE}dataset_chart_${pagePath}_${datasetId}.${visualizationId}`;
}

/**
 * Build the per-dataset custom-indicator-presets key.
 *
 * Used by `src/composables/charts/useIndicatorSelections.js` to persist a
 * user's custom indicator presets per dataset.
 */
export function getIndicatorCustomPresetsKey(datasetId) {
  return `${PERSISTED_STATE_NAMESPACE}indicator_custom_presets_${datasetId}`;
}

/**
 * API-docs Try-It credential. Stored in **sessionStorage** (never
 * localStorage) because a pfk_ API key is a long-lived plaintext credential:
 * tab-scoped, gone when the tab closes. The legacy `pf_api-docs-api-key`
 * localStorage entry is purged on load by
 * `src/features/api-documentation/composables/useApiTester.js`.
 */
export const API_DOCS_TRY_IT_KEY_SESSION_KEY = 'apiDocsTryItApiKey';

/**
 * `{ groupId, at }` of the benchmark group this tab just submitted, in
 * **sessionStorage**. A reload right after submit lands before the group is
 * visible through ES refresh + the run-groups cache; this stamp lets the
 * wizard keep showing "loading" instead of the deleted card for a few seconds.
 */
export const BENCHMARK_GROUP_SUBMIT_SESSION_KEY = 'benchmarkGroupSubmit';

// Keys that come from `usePersistedState(...)` callers — listed here for
// audit even though they're produced by the composable, not constants.
//
//   usePersistedState('recently-viewed-datasets', [])  → pf_recently-viewed-datasets
//   usePersistedState('api-docs-catalog-cart', [])     → pf_api-docs-catalog-cart (API store cart)

// ─────────────────────────────────────────────────────────────────────────
// OUT-OF-NAMESPACE EXCEPTIONS
// These intentionally live OUTSIDE the pf_ namespace because their lifecycle
// is owned by something other than the logout flow. Do not migrate them
// without changing the owning code path.
// ─────────────────────────────────────────────────────────────────────────

export const OUT_OF_NAMESPACE_KEYS = Object.freeze({
  // Auth tokens — cleared by useAuthStore.clearTokens(), not by logout cleanup.
  // Token-refresh interceptors read/write these directly.
  accessToken: 'accessToken', // sessionStorage
  refreshToken: 'refreshToken', // localStorage
  cachedCurrentUser: 'cachedCurrentUser', // localStorage; cleared explicitly
  isUserLoaded: 'isUserLoaded', // sessionStorage; cleared explicitly

  // Theme preferences — must survive logout because they're user-agent
  // preferences (light/dark, color preset) not session state.
  newsLanguages: 'profiteia.newsLanguages', // Reading preference survives logout.
  themePreset: 'themePreset', // localStorage
  themeMode: 'themeMode', // localStorage
  darkMode: 'darkMode', // localStorage (boolean shadow of themeMode)

  // Vuex-legacy settings store key. The new useSettingsStore preserves this
  // exact key so users keep their saved date-picker mode + recent ranges
  // across the Pinia migration.
  profiteiaSettings: 'profiteia.settings', // localStorage

  // Shared-dashboard tokens for unauthenticated viewers of share links.
  // Separate namespace because anonymous shared-dashboard sessions must
  // not be tied to logout (the user isn't logged in).
  sharedVisitor: 'profiteia:shared:visitor', // localStorage
  // Per-token session keys are dynamic: `profiteia:shared:${token.slice(0, 12)}`
  sharedSessionPrefix: 'profiteia:shared:', // dynamic prefix

  // Visitor / fingerprint ID, used for analytics + route-change tracking.
  // Persists across logout so visitor identity is stable.
  visitorId: 'visitorId', // both session + localStorage
});

// ─────────────────────────────────────────────────────────────────────────
// LEGACY KEYS that we auto-migrate. Listed for reference; the migration
// runs once on first read inside useDisplayStore.
// ─────────────────────────────────────────────────────────────────────────

export const LEGACY_KEYS_AUTO_MIGRATED = Object.freeze({
  sidebarCollapsed: 'sidebarCollapsed', // → pf_sidebar-collapsed
  locale: 'locale', // → pf_locale
});

/** Reviewed reservation refunds awaiting scoped readback. */
export const TOKEN_REFUND_COMMAND_PREFIX = `${PERSISTED_STATE_NAMESPACE}admin.token-refund:`;

/** Publication requests awaiting read-only outcome recovery. */
export const PUBLICATION_COMMAND_PREFIX = `${PERSISTED_STATE_NAMESPACE}admin.publication:`;

/** Reviewed promotion offer changes awaiting confirmed readback. */
export const PROMOTION_OFFER_COMMAND_PREFIX = `${PERSISTED_STATE_NAMESPACE}admin.promotion-offer:`;

/** Actor/API-scoped interrupted generic broadcast; cleared on logout. */
export const NOTIFICATION_COMMAND_PREFIX = `${PERSISTED_STATE_NAMESPACE}admin.notification:`;

/** Actor/API/account-scoped restriction command; cleared on logout. */
export const ACCOUNT_RESTRICTION_COMMAND_PREFIX = `${PERSISTED_STATE_NAMESPACE}admin.account-restriction:`;

/** A signed-in user's unsent tool form, per user and tool (PI-713). Cleared on logout. */
export const TOOL_DRAFT_PREFIX = `${PERSISTED_STATE_NAMESPACE}tools.draft:`;

/** Operator-scoped admin wizard inputs. Cleared on logout; reviews are never persisted here. */
export const ADMIN_WIZARD_DRAFT_PREFIX = `${PERSISTED_STATE_NAMESPACE}admin.wizard-draft:`;
